import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
	closestCenter,
	DndContext,
	DragOverlay,
	KeyboardSensor,
	PointerSensor,
	pointerWithin,
	useSensor,
	useSensors,
} from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import { SortableContext, sortableKeyboardCoordinates } from "@dnd-kit/sortable"
import { useNavigate, useParams } from "@tanstack/react-router"
import { IconHouse6Fill18 } from "nucleo-ui-fill-18"
import { IconFilePlusOutline18 as FilePlus } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { isLooseGroup } from "@productlane/lib/src/help-center/loose-group"
import { IconButton, Tooltip, useBreakpoints } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import type { Positioned } from "./plan-insert"
import type {
	ArticleWithSubpages,
	DragItem,
	GroupHeaderDropTarget,
	GroupWithArticles,
} from "./types"
import type {
	CollisionDetection,
	DragEndEvent,
	DragMoveEvent,
	DragStartEvent,
	UniqueIdentifier,
} from "@dnd-kit/core"
import { NavSectionHeader } from "@/components/shared/nav-section-header"
import { RoleCheck } from "@/components/shared/role-check"
import { useShortcut } from "@/lib"
import { generateId } from "@/lib/id"
import { useZero } from "@/lib/zero"
import {
	getActivePortalInstanceId,
	useActivePortalInstanceStore,
} from "@/stores/active-portal-instance-store"
import {
	DOCS_SECTIONS,
	useDocNavStore,
	useIsDocsSectionOpen,
} from "@/stores/docs/docs-nav-store"
import { AddGroupButton } from "./add-group-button"
import {
	ApiReferenceNavGroups,
	ApiReferenceOverviewRow,
} from "./api-reference-nav"
import { DocsNavPlaceholderRow } from "./docs-nav-placeholder-row"
import { DragPreview, MeasureBridge } from "./drag-preview"
import { useHelpCenterArticlesSettled } from "./help-center-context"
import {
	createDropSignalStore,
	DropSignalProvider,
} from "./help-center-drop-indicator"
import { HelpCenterNavGroup } from "./help-center-nav-group"
import {
	AgentArticlesSection,
	InternalArticlesSection,
} from "./hidden-articles-section"
import { collectHiddenArticles } from "./hidden-tier-folders"
import { withSubpagesInNavOrder } from "./nav-keyboard-order"
import { NavLandingRow } from "./nav-landing-row"
import { noSortShift } from "./no-sort-shift"
import { planInsert } from "./plan-insert"
import { toPublicTreeGroups } from "./public-tree-groups"
import { SourcesSection } from "./sources-section"
import { planTopLevelPlacement } from "./top-level-placement"
import { useArticleSuggestions } from "./use-article-suggestions"
import { useCreateArticle } from "./use-create-article"
import { useDelayedUnmount } from "./use-delayed-unmount"
import {
	groupTabId,
	homeReplacementArticleId,
	useDocsTabs,
	useSelectDocsTab,
} from "./use-docs-tabs"

// How long an article must dwell over another article before it promotes from
// "reorder next to it" to "nest inside it as a subpage".
const NEST_DWELL_MS = 600

// How long an article must hover a collapsed group header before that group
// springs open, so you can drop into a group without opening it first.
const SPRING_OPEN_MS = 450

export function HelpCenterNavContent({
	articleGroups: allArticleGroups,
	isHomeActive = false,
}: {
	articleGroups: readonly GroupWithArticles[]
	isHomeActive?: boolean
}) {
	const zero = useZero()
	const createArticle = useCreateArticle({
		articleGroups: allArticleGroups,
	})
	const articlesSettled = useHelpCenterArticlesSettled()

	const params = useParams({ strict: false })
	const navigate = useNavigate()
	const selectedItemRef = useRef<HTMLLIElement>(null)
	const docsNavStore = useDocNavStore()
	const isPublicOpen = useIsDocsSectionOpen(DOCS_SECTIONS.public)
	const renderPublicTree = useDelayedUnmount(isPublicOpen, 250)

	// Hidden articles and links render in their own sections below, so the group
	// tree stays the public tree and no article appears twice. Subpages are
	// filtered too: a hidden subpage under a public parent belongs in a section.
	// With tabs, the public tree shows only the active tab's groups; Agent and
	// Internal stay complete because readers never see them.
	const { tabs, activeTabId, activeTab } = useDocsTabs()
	const selectTab = useSelectDocsTab()
	const articleGroups = useMemo(
		() =>
			toPublicTreeGroups(allArticleGroups).filter(
				(group) => groupTabId(group, tabs) === activeTabId,
			),
		[allArticleGroups, tabs, activeTabId],
	)

	// A top-level page that comes first replaces the docs home, so the Home row goes.
	const hasDocsHome = useMemo(
		() =>
			activeTabId === null &&
			homeReplacementArticleId(allArticleGroups, tabs) === null,
		[activeTabId, allArticleGroups, tabs],
	)

	const publicArticles = useMemo(() => {
		return articleGroups.flatMap((group) => group.helpCenterArticles)
	}, [articleGroups])

	// Keyboard order follows render order: public groups, then Agent, then
	// Internal. Built from the unfiltered groups so hidden rows are reachable
	// with j/k.
	const allArticles = useMemo(
		() =>
			withSubpagesInNavOrder([
				...publicArticles,
				...collectHiddenArticles(allArticleGroups, "UNLISTED"),
				...collectHiddenArticles(allArticleGroups, "INTERNAL"),
			]),
		[publicArticles, allArticleGroups],
	)

	const groupIds = useMemo(
		() => articleGroups.map((g) => g.id),
		[articleGroups],
	)

	// Suggestion rows (docs proposals) appear above the article list in the
	// sidebar, so J/K navigation iterates through them first, then articles.
	const { visibleRows: suggestionRows } = useArticleSuggestions()
	// Mirrors the gate in help-center-nav.tsx.
	const isMainPortal = useActivePortalInstanceStore(
		(s) => s.activePortalInstanceId === null,
	)
	const showsSuggestions = isMainPortal && suggestionRows.length > 0

	type NavItem =
		| { kind: "suggestion"; route: string; suggestionKey: string }
		| { kind: "article"; route: string; articleId: string }

	const navItems = useMemo<NavItem[]>(() => {
		const suggestionItems: NavItem[] = suggestionRows.map((row) => {
			if (row.articleId) {
				return {
					kind: "suggestion",
					route: `/edit-docs/review/${row.articleId}`,
					suggestionKey: `review:${row.articleId}`,
				}
			}
			const firstProposalId = row.proposals[0]?.id ?? row.key
			return {
				kind: "suggestion",
				route: `/edit-docs/review-new/${firstProposalId}`,
				suggestionKey: `review-new:${firstProposalId}`,
			}
		})
		const articleItems: NavItem[] = allArticles.map((article) => ({
			kind: "article",
			route: `/edit-docs/${article.id}`,
			articleId: article.id,
		}))
		return [...suggestionItems, ...articleItems]
	}, [suggestionRows, allArticles])

	// Resolve the active row across all three possible URL shapes:
	// `/edit-docs/review/:articleId`, `/edit-docs/review-new/:proposalId`, and
	// `/edit-docs/:id`.
	const currentIndex = useMemo(() => {
		if (params.articleId) {
			return navItems.findIndex(
				(item) =>
					item.kind === "suggestion" &&
					item.suggestionKey === `review:${params.articleId}`,
			)
		}
		if (params.proposalId) {
			return navItems.findIndex(
				(item) =>
					item.kind === "suggestion" &&
					item.suggestionKey === `review-new:${params.proposalId}`,
			)
		}
		if (params.id) {
			return navItems.findIndex(
				(item) => item.kind === "article" && item.articleId === params.id,
			)
		}
		return -1
	}, [navItems, params.articleId, params.proposalId, params.id])

	// Scroll selected item into view and open its group
	useEffect(() => {
		if (selectedItemRef.current) {
			selectedItemRef.current.scrollIntoView({
				behavior: "instant",
				block: "nearest",
			})
		}

		// Open the group containing the selected article only if not manually closed
		if (params.id && allArticles.length > 0) {
			const selectedArticle = allArticles.find(
				(article) => article.id === params.id,
			)
			if (selectedArticle) {
				const rootId = selectedArticle.parentArticleId ?? selectedArticle.id
				if (selectedArticle.parentArticleId && docsNavStore.isArrowNavigation) {
					docsNavStore.openArticle(selectedArticle.parentArticleId)
				}
				const group = articleGroups.find((group) =>
					group.helpCenterArticles.some((article) => article.id === rootId),
				)
				// Only auto-open if the group is not manually closed by user (unless navigating with arrows)
				if (
					group &&
					!docsNavStore.isGroupOpen(group.id) &&
					(!docsNavStore.manuallyClosedGroups.includes(group.id) ||
						docsNavStore.isArrowNavigation)
				) {
					docsNavStore.openGroup(group.id)
				}
			}
		}

		// Reset arrow navigation flag after processing
		if (docsNavStore.isArrowNavigation) {
			docsNavStore.setIsArrowNavigation(false)
		}
	}, [params.id, allArticles, articleGroups, docsNavStore])

	const goToItem = (item: NavItem) => {
		docsNavStore.setIsArrowNavigation(true)
		void navigate({ href: item.route })
	}

	const navigateToNext = () => {
		if (navItems.length === 0) return
		const nextIndex =
			currentIndex === -1 ? 0 : (currentIndex + 1) % navItems.length
		const nextItem = navItems[nextIndex]
		if (nextItem) goToItem(nextItem)
	}

	const navigateToPrevious = () => {
		if (navItems.length === 0) return
		if (currentIndex === 0) {
			docsNavStore.clearOpenGroups()
			void navigate({ to: "/edit-docs" })
			return
		}
		if (currentIndex === -1) {
			const lastItem = navItems[navItems.length - 1]
			if (lastItem) goToItem(lastItem)
			return
		}
		const prevItem = navItems[currentIndex - 1]
		if (prevItem) goToItem(prevItem)
	}

	useShortcut("down, j", () => navigateToNext(), {
		preventDefault: true,
		enabled: navItems.length > 0,
	})
	useShortcut("up, k", () => navigateToPrevious(), {
		preventDefault: true,
		enabled: navItems.length > 0,
	})

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	)

	const dropStore = useMemo(createDropSignalStore, [])
	// The row + side the pointer is currently over, updated on every drag move.
	// handleDragEnd reads this (not dnd-kit's `over`) so the drop lands exactly
	// where the insertion line is drawn.
	const dropRef = useRef<{
		overId: string
		side: "before" | "after"
	} | null>(null)
	const dwellRef = useRef<{ overId: string; timer: number } | null>(null)
	const nestedRef = useRef(false)
	// Group header the pointer is inside, recorded during collision detection
	// because that is the only place with measured rects for every container.
	const hoveredGroupRef = useRef<string | null>(null)
	const springRef = useRef<{ groupId: string; timer: number } | null>(null)
	const measureRef = useRef<((ids: UniqueIdentifier[]) => void) | null>(null)
	const [activeDrag, setActiveDrag] = useState<DragItem | null>(null)

	// Re-measure across the next few frames: collapsing a section and springing
	// a group open both animate, so a single measurement would catch mid-flight
	// geometry.
	const remeasure = useCallback(() => {
		let frames = 0
		const tick = () => {
			measureRef.current?.([])
			if (++frames < 18) requestAnimationFrame(tick)
		}
		requestAnimationFrame(tick)
	}, [])

	const clearDwell = useCallback(() => {
		if (dwellRef.current) {
			window.clearTimeout(dwellRef.current.timer)
			dwellRef.current = null
		}
		nestedRef.current = false
	}, [])

	const clearSpring = useCallback(() => {
		if (springRef.current) {
			window.clearTimeout(springRef.current.timer)
			springRef.current = null
		}
	}, [])

	const resetDrag = useCallback(() => {
		clearDwell()
		clearSpring()
		hoveredGroupRef.current = null
		dropRef.current = null
		dropStore.set(null)
	}, [clearDwell, clearSpring, dropStore])

	const findArticleAnywhere = useCallback(
		(
			id: string,
		): {
			article: ArticleWithSubpages
			groupId: string
			parentArticleId: string | null
		} | null => {
			for (const group of articleGroups) {
				for (const article of group.helpCenterArticles) {
					if (article.id === id) {
						return {
							article,
							groupId: group.id,
							parentArticleId: article.parentArticleId ?? null,
						}
					}
					for (const subpage of article.subpages ?? []) {
						if (subpage.id === id) {
							return {
								article: subpage,
								groupId: group.id,
								parentArticleId: article.id,
							}
						}
					}
				}
			}
			return null
		},
		[articleGroups],
	)

	// Scope collisions to the active item's level: a dragged group only ever
	// resolves to another group, a dragged article only to another article.
	// Without this, closestCenter mixes groups + every article + subpage into
	// one pool, which made groups refuse to move and articles land in the wrong
	// place.
	const collisionDetection = useCallback<CollisionDetection>((args) => {
		const activeType = (args.active.data.current as DragItem | undefined)?.type
		if (activeType === "article") {
			// Top-level pages have no header to spring open.
			const groupHit = pointerWithin({
				...args,
				droppableContainers: args.droppableContainers.filter((container) => {
					const data = container.data.current as DragItem | undefined
					return data?.type === "group" && !isLooseGroup(data.group)
				}),
			})[0]
			hoveredGroupRef.current = groupHit ? String(groupHit.id) : null
		}
		// An article also targets group headers: dropping on one moves it to the
		// top level of the tree, beside that group.
		const scoped = {
			...args,
			droppableContainers: args.droppableContainers.filter((container) => {
				if (container.id === args.active.id) return false
				const type = (
					container.data.current as DragItem | GroupHeaderDropTarget | undefined
				)?.type
				return (
					type === activeType ||
					(activeType === "article" && type === "group-header")
				)
			}),
		}
		return closestCenter(scoped)
	}, [])

	const handleDragStart = (event: DragStartEvent) => {
		resetDrag()
		const data = event.active.data.current as DragItem | undefined
		setActiveDrag(data ?? null)
		// Collapse the dragged section so it travels as one row. This changes the
		// layout after dnd-kit's drag-start measurement, hence the re-measure.
		if (data?.type === "group" && docsNavStore.isGroupOpen(data.group.id)) {
			docsNavStore.closeGroup(data.group.id)
		} else if (
			data?.type === "article" &&
			docsNavStore.isArticleOpen(data.article.id)
		) {
			docsNavStore.toggleArticle(data.article.id)
		}
		remeasure()
	}

	// Hovering a collapsed group header opens it after a beat, so an article can
	// be dropped into a group that was closed when the drag began.
	const runSpringOpen = (activeType: DragItem["type"] | undefined) => {
		const groupId = hoveredGroupRef.current
		if (
			activeType !== "article" ||
			!groupId ||
			docsNavStore.isGroupOpen(groupId)
		) {
			clearSpring()
			return
		}
		if (springRef.current?.groupId === groupId) return
		clearSpring()
		springRef.current = {
			groupId,
			timer: window.setTimeout(() => {
				docsNavStore.openGroup(groupId)
				remeasure()
			}, SPRING_OPEN_MS),
		}
	}

	// On every move: draw the insertion line on the hovered row (before/after
	// by pointer position within the row), and separately run a dwell timer that
	// promotes a top-level article to a nesting target. The line shows the exact
	// drop position; dwelling switches to "nest inside".
	const handleDragMove = (event: DragMoveEvent) => {
		const { active, over, activatorEvent, delta } = event
		const activeData = active.data.current as DragItem | undefined
		runSpringOpen(activeData?.type)

		if (!over || active.id === over.id) {
			clearDwell()
			dropRef.current = null
			dropStore.set(null)
			return
		}
		const overData = over.data.current as
			| DragItem
			| GroupHeaderDropTarget
			| undefined
		const isHeaderDrop =
			activeData?.type === "article" && overData?.type === "group-header"
		if (!activeData || (overData?.type !== activeData.type && !isHeaderDrop)) {
			clearDwell()
			dropRef.current = null
			dropStore.set(null)
			return
		}

		const pointerY = (activatorEvent as PointerEvent).clientY + delta.y
		const rect = over.rect
		const side: "before" | "after" =
			pointerY < rect.top + rect.height / 2 ? "before" : "after"
		const overId = String(over.id)

		// If we're currently nesting into this row, keep showing that.
		if (nestedRef.current && dwellRef.current?.overId === overId) {
			dropStore.set({ kind: "nest", id: overId })
			dropRef.current = null
			return
		}

		dropRef.current = { overId, side }
		dropStore.set({ kind: side, id: overId })

		// Dwell-to-nest only applies to a top-level article over another
		// top-level article in the same group.
		const canNest =
			activeData.type === "article" &&
			!activeData.parentArticleId &&
			overData?.type === "article" &&
			!overData.parentArticleId &&
			overData.groupId === activeData.groupId
		if (!canNest) {
			clearDwell()
			return
		}
		if (dwellRef.current?.overId === overId) return
		clearDwell()
		dwellRef.current = {
			overId,
			timer: window.setTimeout(() => {
				nestedRef.current = true
				dropRef.current = null
				dropStore.set({ kind: "nest", id: overId })
			}, NEST_DWELL_MS),
		}
	}

	// Fire-and-forget: Zero applies the move optimistically, then syncs to
	// Postgres in the background. Awaiting would block the drop frame on the
	// re-render, which is what made dropping feel heavy.
	const moveArticle = (
		source: NonNullable<ReturnType<typeof findArticleAnywhere>>,
		target: {
			groupId: string
			parentArticleId: string | null
			siblings: readonly Positioned[]
			insertIndex: number
		},
	) => {
		const { order, siblings } = planInsert(target.siblings, target.insertIndex)
		const carriesSubpages =
			source.parentArticleId === null &&
			target.parentArticleId === null &&
			source.groupId !== target.groupId
		void zero.mutate(
			mutators.helpCenterArticles.move({
				id: source.article.id,
				helpCenterGroupId: target.groupId,
				parentArticleId: target.parentArticleId,
				order,
				previousGroupId: source.groupId,
				previousParentArticleId: source.parentArticleId,
				subpageIds: carriesSubpages
					? (source.article.subpages ?? []).map((s) => s.id)
					: [],
				siblings,
			}),
		)
	}

	// Out of every group, between `articleGroups[index - 1]` and `[index]`: into
	// the run of top-level pages already there, or a new unnamed group.
	const moveToTopLevel = (
		source: NonNullable<ReturnType<typeof findArticleAnywhere>>,
		index: number,
	) => {
		const placement = planTopLevelPlacement(articleGroups, index)
		if (placement.kind === "existing") {
			const list =
				articleGroups.find((g) => g.id === placement.groupId)
					?.helpCenterArticles ?? []
			const oldIndex = list.findIndex((a) => a.id === source.article.id)
			const rest = list.filter((a) => a.id !== source.article.id)
			const insertIndex = placement.at === "end" ? rest.length : 0
			if (oldIndex !== -1 && oldIndex === insertIndex) return
			moveArticle(source, {
				groupId: placement.groupId,
				parentArticleId: null,
				siblings: rest,
				insertIndex,
			})
			return
		}
		const groupId = generateId()
		void zero.mutate(
			mutators.helpCenterGroups.create({
				id: groupId,
				name: "",
				order: placement.order,
				portalInstanceId: getActivePortalInstanceId(),
				tabId: activeTabId,
			}),
		)
		if (placement.siblings.length > 0) {
			void zero.mutate(
				mutators.helpCenterGroups.reorder({ items: placement.siblings }),
			)
		}
		moveArticle(source, {
			groupId,
			parentArticleId: null,
			siblings: [],
			insertIndex: 0,
		})
	}

	const handleDragEnd = (event: DragEndEvent) => {
		setActiveDrag(null)
		const { active, over } = event
		const activeData = active.data.current as DragItem | undefined
		const overData = over?.data.current as
			| DragItem
			| GroupHeaderDropTarget
			| undefined
		const nested = nestedRef.current
		const drop = dropRef.current
		resetDrag()

		if (!over || active.id === over.id) return

		// Dwell-to-nest committed: move the dragged article into the hovered
		// article's subpage list (appended at the end).
		if (
			nested &&
			activeData?.type === "article" &&
			overData?.type === "article"
		) {
			const source = findArticleAnywhere(String(active.id))
			const parent = findArticleAnywhere(String(over.id))
			if (!source || !parent || parent.parentArticleId) return
			const subpages = (parent.article.subpages ?? []).filter(
				(s) => s.id !== source.article.id,
			)
			moveArticle(source, {
				groupId: parent.groupId,
				parentArticleId: parent.article.id,
				siblings: subpages,
				insertIndex: subpages.length,
			})
			docsNavStore.openArticle(parent.article.id)
			return
		}

		const side = drop?.overId === String(over.id) ? drop.side : "before"

		// Above a header is the top level before that group. Below it is the
		// group's first row while it is open, the top level after it once closed.
		if (activeData?.type === "article" && overData?.type === "group-header") {
			const source = findArticleAnywhere(String(active.id))
			const index = articleGroups.findIndex((g) => g.id === overData.group.id)
			const target = articleGroups[index]
			if (!source || !target) return
			if (side === "after" && docsNavStore.isGroupOpen(target.id)) {
				const rest = target.helpCenterArticles.filter(
					(a) => a.id !== source.article.id,
				)
				moveArticle(source, {
					groupId: target.id,
					parentArticleId: null,
					siblings: rest,
					insertIndex: 0,
				})
				return
			}
			moveToTopLevel(source, side === "before" ? index : index + 1)
			return
		}

		if (activeData?.type === "group") {
			if (overData?.type !== "group") return
			const rest = articleGroups.filter((g) => g.id !== active.id)
			const overPos = rest.findIndex((g) => g.id === String(over.id))
			if (overPos === -1) return
			const { order, siblings } = planInsert(
				rest,
				side === "after" ? overPos + 1 : overPos,
			)
			void zero.mutate(
				mutators.helpCenterGroups.reorder({
					items: [{ id: String(active.id), order }, ...siblings],
				}),
			)
			return
		}

		if (activeData?.type !== "article" || overData?.type !== "article") return
		const source = findArticleAnywhere(String(active.id))
		const overLoc = findArticleAnywhere(String(over.id))
		if (!source || !overLoc) return

		const targetGroupId = overLoc.groupId
		const targetParentId = overLoc.parentArticleId
		if (targetParentId === source.article.id) return

		// Moving into a different group only works when that group is open, so
		// the drop lands on a visible row rather than a collapsed section.
		// Top-level pages have no header to collapse.
		const targetGroup = articleGroups.find((g) => g.id === targetGroupId)
		if (
			source.groupId !== targetGroupId &&
			!docsNavStore.isGroupOpen(targetGroupId) &&
			!(targetGroup && isLooseGroup(targetGroup))
		) {
			return
		}

		const list = targetParentId
			? (findArticleAnywhere(targetParentId)?.article.subpages ?? [])
			: (articleGroups.find((g) => g.id === targetGroupId)
					?.helpCenterArticles ?? [])
		// Index into the list minus the dragged row, which is what planInsert
		// reasons about. Leaving the source container leaves a gap in its order
		// values; gaps are harmless, so nothing renumbers the list it left.
		const oldIndex = list.findIndex((a) => a.id === source.article.id)
		const rest = oldIndex === -1 ? list : list.filter((_, i) => i !== oldIndex)
		const overPos = rest.findIndex((a) => a.id === over.id)
		if (overPos === -1) return
		const insertIndex = side === "after" ? overPos + 1 : overPos
		if (oldIndex !== -1 && insertIndex === oldIndex) return

		moveArticle(source, {
			groupId: targetGroupId,
			parentArticleId: targetParentId,
			siblings: rest,
			insertIndex,
		})
		if (targetParentId) docsNavStore.openArticle(targetParentId)
	}

	return (
		<ul
			className={cn(
				"flex list-none flex-col gap-px !overflow-x-hidden px-2.5 pb-3",
				// Suggestions carries the 20px top gap when it renders; otherwise
				// Public is the first thing in the sidebar and owns it.
				showsSuggestions ? "pt-3" : "pt-5",
			)}
		>
			<li className="group/docs-section flex flex-col gap-px">
				<NavSectionHeader
					label="Public"
					isOpen={isPublicOpen}
					onToggle={() => docsNavStore.toggleSection(DOCS_SECTIONS.public)}
					action={
						<>
							<NewTopLevelArticleButton
								onClick={() =>
									createArticle({ visibility: "PUBLIC", topLevel: true })
								}
							/>
							<AddGroupButton groups={allArticleGroups} variant="icon" />
						</>
					}
				/>
				<div
					inert={!isPublicOpen}
					className={cn(
						"ease-[cubic-bezier(0.23,1,0.32,1)] grid transition-[grid-template-rows,opacity] duration-200",
						isPublicOpen
							? "grid-rows-[1fr] opacity-100"
							: "grid-rows-[0fr] opacity-40",
					)}
				>
					<div className="min-h-0 overflow-hidden">
						{activeTab?.kind === "API_REFERENCE" && (
							<ApiReferenceOverviewRow tabId={activeTab.id} />
						)}
						{/* Only the default tab has a home on the docs site. A phone shows
						    this tree in place of the home, so the row has nowhere to go there. */}
						{hasDocsHome && (
							<NavLandingRow
								icon={
									<IconHouse6Fill18 className="size-3.5 flex-none text-secondary opacity-90" />
								}
								label="Home"
								isActive={isHomeActive}
								onClick={() => selectTab(null)}
								className="mb-2 max-sm:hidden"
							/>
						)}
						<DndContext
							sensors={sensors}
							onDragStart={handleDragStart}
							onDragMove={handleDragMove}
							onDragEnd={handleDragEnd}
							onDragCancel={() => {
								setActiveDrag(null)
								resetDrag()
							}}
							collisionDetection={collisionDetection}
							modifiers={[restrictToVerticalAxis]}
							// Groups springing open mid-drag changes the scroll height, and
							// dnd-kit's shift compensation reacts to that by scrolling again.
							autoScroll={{
								layoutShiftCompensation: false,
								threshold: { x: 0, y: 0.12 },
							}}
						>
							<DropSignalProvider value={dropStore}>
								<SortableContext items={groupIds} strategy={noSortShift}>
									{renderPublicTree &&
										articleGroups.map((group) => (
											<HelpCenterNavGroup
												key={group.id}
												group={group}
												selectedItemRef={selectedItemRef}
											/>
										))}
								</SortableContext>
							</DropSignalProvider>
							<MeasureBridge measureRef={measureRef} />
							<DragOverlay dropAnimation={null}>
								{activeDrag ? <DragPreview item={activeDrag} /> : null}
							</DragOverlay>
						</DndContext>
						{activeTab?.kind === "API_REFERENCE" && (
							<ApiReferenceNavGroups tabId={activeTab.id} />
						)}
						{publicArticles.length === 0 &&
							articlesSettled &&
							activeTab?.kind !== "API_REFERENCE" && (
								<RoleCheck not={["VIEWER"]}>
									<DocsNavPlaceholderRow
										label="New article"
										onClick={() => createArticle({ visibility: "PUBLIC" })}
									/>
								</RoleCheck>
							)}
					</div>
				</div>
			</li>
			<AgentArticlesSection articleGroups={allArticleGroups} />
			<InternalArticlesSection articleGroups={allArticleGroups} />
			<SourcesSection />
		</ul>
	)
}

// A page outside every group, at the bottom of the tab's tree.
function NewTopLevelArticleButton({ onClick }: { onClick: () => void }) {
	const { isMobile } = useBreakpoints()
	return (
		<RoleCheck not={["VIEWER"]}>
			<Tooltip label="New article">
				<IconButton
					Icon={FilePlus}
					variant="ghost"
					size="sm"
					aria-label="New article"
					onClick={onClick}
					className={cn(
						"h-5 w-5 flex-none text-tertiary hover:text-primary focus-visible:opacity-100",
						isMobile
							? "opacity-100"
							: "opacity-0 group-hover/section:opacity-100",
					)}
				/>
			</Tooltip>
		</RoleCheck>
	)
}
